import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createFamily } from '../../services/firebase';

const STEPS = ['student', 'allowance', 'match', 'start', 'invite'];

const ParentEnrollment = () => {
  const { authUser, userDoc } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [studentName, setStudentName] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [allowanceAmount, setAllowanceAmount] = useState('');
  const [frequency, setFrequency] = useState('weekly');
  const [matchEnabled, setMatchEnabled] = useState(true);
  const [matchType, setMatchType] = useState('percentage');
  const [matchValue, setMatchValue] = useState('25');
  const [monthlyCap, setMonthlyCap] = useState('');
  const [startOnSunday, setStartOnSunday] = useState(true); // default: wait for Sunday
  const [inviteLink, setInviteLink] = useState('');

  const progress = ((step + 1) / STEPS.length) * 100;
  const firstName = studentName.trim().split(' ')[0] || 'your student';

  const handleStudentNext = (e) => {
    e.preventDefault();
    if (!studentName.trim()) return;
    setStep(1);
  };

  const handleAllowanceNext = (e) => {
    e.preventDefault();
    if (!allowanceAmount || parseFloat(allowanceAmount) <= 0) return;
    setStep(2);
  };

  const handleMatchNext = (e) => {
    e.preventDefault();
    setStep(3); // go to start timing step
  };

  const handleStartNext = () => {
    setStep(4); // go to create + invite
  };

  const handleCreate = async () => {
    setError('');
    setLoading(true);
    try {
      const { familyId, inviteToken } = await createFamily(authUser.uid, {
        studentName: studentName.trim(),
        inviteEmail: studentEmail.trim(),
        allowance: {
          amount: parseFloat(allowanceAmount),
          frequency,
          startDate: new Date(),
        },
        match: {
          enabled: matchEnabled,
          type: matchType,
          value: parseFloat(matchValue) || 0,
          monthlyCap: parseFloat(monthlyCap) || 0,
        },
        startOnSunday,
        walletStarted: !startOnSunday,
      });

      const link = `${process.env.REACT_APP_BASE_URL}/invite/${inviteToken}`;
      setInviteLink(link);
      setStep(4); // show invite step
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => navigate('/enrollment-success');

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
  };

  return (
    <div className="screen screen--no-nav">
      {/* Progress */}
      <div style={{ marginBottom: 32 }}>
        <div className="progress-bar">
          <div className="progress-bar__fill" style={{ width: `${progress}%` }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          marginTop: 8, fontSize: '0.8125rem', color: 'var(--color-text-3)' }}>
          <span>Step {step + 1} of {STEPS.length}</span>
          <span>{Math.round(progress)}% complete</span>
        </div>
      </div>

      {/* Step 0: Student Info */}
      {step === 0 && (
        <div className="animate-in">
          <h2 style={{ marginBottom: 8 }}>Tell us about your student</h2>
          <p style={{ color: 'var(--color-text-2)', marginBottom: 32 }}>
            Their name personalizes the app — they'll see "{firstName || 'Alex'}'s Wallet" on their home screen.
          </p>
          <form onSubmit={handleStudentNext} className="stack stack-5">
            <div className="form-group">
              <label className="form-label">Student's full name</label>
              <input
                className="form-input"
                type="text"
                placeholder="Alex Smith"
                value={studentName}
                onChange={e => setStudentName(e.target.value)}
                required
                autoFocus
              />
              {studentName && (
                <div className="form-hint">
                  Their app will be called <strong>"{studentName.trim().split(' ')[0]}'s Wallet"</strong>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Student's email <span style={{ fontWeight: 400, color: 'var(--color-text-3)' }}>(optional)</span></label>
              <input
                className="form-input"
                type="email"
                placeholder="alex@university.edu"
                value={studentEmail}
                onChange={e => setStudentEmail(e.target.value)}
              />
              <div className="form-hint">We'll send the invite link here too</div>
            </div>

            <button type="submit" className="btn btn--primary btn--full btn--lg"
              disabled={!studentName.trim()} style={{ marginTop: 8 }}>
              Continue →
            </button>
          </form>
        </div>
      )}

      {/* Step 1: Allowance */}
      {step === 1 && (
        <div className="animate-in">
          <button onClick={() => setStep(0)} style={backStyle}>← Back</button>
          <h2 style={{ marginBottom: 8 }}>Set the allowance</h2>
          <p style={{ color: 'var(--color-text-2)', marginBottom: 32 }}>
            How much will you send {firstName}, and how often?
          </p>
          <form onSubmit={handleAllowanceNext} className="stack stack-5">
            <div className="form-group">
              <label className="form-label">Allowance amount</label>
              <div className="input-with-prefix">
                <span className="input-prefix">$</span>
                <input
                  className="form-input"
                  type="number"
                  min="1"
                  step="0.01"
                  placeholder="150.00"
                  value={allowanceAmount}
                  onChange={e => setAllowanceAmount(e.target.value)}
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Frequency</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                {['weekly', 'biweekly', 'monthly'].map(f => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFrequency(f)}
                    style={{
                      padding: '12px 8px',
                      borderRadius: 'var(--radius-md)',
                      border: '2px solid',
                      borderColor: frequency === f ? 'var(--color-primary)' : 'var(--color-border)',
                      background: frequency === f ? 'var(--color-primary-pale)' : 'white',
                      color: frequency === f ? 'var(--color-primary)' : 'var(--color-text)',
                      fontWeight: frequency === f ? 600 : 400,
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-body)',
                      transition: 'all 150ms ease',
                      textTransform: 'capitalize',
                    }}
                  >
                    {f === 'biweekly' ? 'Bi-weekly' : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {allowanceAmount && (
              <div className="card--accent" style={{ padding: 16 }}>
                <p style={{ fontSize: '0.9rem', color: 'var(--color-primary)', fontWeight: 500 }}>
                  📅 You'll send {firstName} <strong>${parseFloat(allowanceAmount).toFixed(2)}</strong> {frequency === 'biweekly' ? 'every two weeks' : frequency}
                </p>
              </div>
            )}

            <button type="submit" className="btn btn--primary btn--full btn--lg"
              disabled={!allowanceAmount} style={{ marginTop: 8 }}>
              Continue →
            </button>
          </form>
        </div>
      )}

      {/* Step 2: Match Settings */}
      {step === 2 && (
        <div className="animate-in">
          <button onClick={() => setStep(1)} style={backStyle}>← Back</button>
          <h2 style={{ marginBottom: 8 }}>Savings match</h2>
          <p style={{ color: 'var(--color-text-2)', marginBottom: 32 }}>
            Optionally match {firstName}'s savings to incentivize good habits. You can change this anytime.
          </p>
          <form onSubmit={handleMatchNext} className="stack stack-5">

            <div className="toggle-wrapper">
              <div>
                <div style={{ fontWeight: 600 }}>Enable savings match</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-text-2)', marginTop: 2 }}>
                  Reward {firstName} for saving
                </div>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={matchEnabled}
                  onChange={e => setMatchEnabled(e.target.checked)}
                />
                <span className="toggle-slider" />
              </label>
            </div>

            {matchEnabled && (
              <div className="stack stack-5 animate-in">
                <div className="form-group">
                  <label className="form-label">Match type</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {[
                      { value: 'percentage', label: 'Percentage', sub: 'e.g. 25% of savings' },
                      { value: 'fixed', label: 'Fixed amount', sub: 'e.g. $1 per $1 saved' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setMatchType(opt.value)}
                        style={{
                          padding: '14px 12px',
                          borderRadius: 'var(--radius-md)',
                          border: '2px solid',
                          borderColor: matchType === opt.value ? 'var(--color-primary)' : 'var(--color-border)',
                          background: matchType === opt.value ? 'var(--color-primary-pale)' : 'white',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontFamily: 'var(--font-body)',
                          transition: 'all 150ms ease',
                        }}
                      >
                        <div style={{ fontWeight: 600, color: matchType === opt.value ? 'var(--color-primary)' : 'var(--color-text)' }}>
                          {opt.label}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', marginTop: 2 }}>
                          {opt.sub}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    {matchType === 'percentage' ? 'Match percentage' : 'Match amount per dollar saved'}
                  </label>
                  <div className="input-with-prefix">
                    <span className="input-prefix">{matchType === 'percentage' ? '%' : '$'}</span>
                    <input
                      className="form-input"
                      type="number"
                      min="1"
                      max={matchType === 'percentage' ? 200 : undefined}
                      step={matchType === 'percentage' ? 1 : 0.25}
                      placeholder={matchType === 'percentage' ? '25' : '0.50'}
                      value={matchValue}
                      onChange={e => setMatchValue(e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Monthly cap <span style={{ fontWeight: 400, color: 'var(--color-text-3)' }}>(optional)</span>
                  </label>
                  <div className="input-with-prefix">
                    <span className="input-prefix">$</span>
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step="1"
                      placeholder="No cap"
                      value={monthlyCap}
                      onChange={e => setMonthlyCap(e.target.value)}
                    />
                  </div>
                  <div className="form-hint">Maximum match you'll contribute per month</div>
                </div>

                {matchValue && (
                  <div className="card--accent" style={{ padding: 16 }}>
                    <p style={{ fontSize: '0.9rem', color: 'var(--color-primary)', fontWeight: 500 }}>
                      🎯 If {firstName} saves $100,{' '}
                      {matchType === 'percentage'
                        ? `you'll match $${(100 * parseFloat(matchValue || 0) / 100).toFixed(0)}`
                        : `you'll add $${(100 * parseFloat(matchValue || 0)).toFixed(0)}`}
                      {monthlyCap ? ` (capped at $${monthlyCap}/mo)` : ''}
                    </p>
                  </div>
                )}
              </div>
            )}

            {error && <div className="alert alert--error">{error}</div>}

            <button
              type="submit"
              className="btn btn--primary btn--full btn--lg"
              disabled={loading}
              style={{ marginTop: 8 }}
            >
              Continue →
            </button>
          </form>
        </div>
      )}

      {/* Step 3: Start Timing */}
      {step === 3 && (
        <div className="animate-in">
          <button onClick={() => setStep(2)} style={backStyle}>← Back</button>
          <h2 style={{ marginBottom: 8 }}>When should {firstName}'s first week start?</h2>
          <p style={{ color: 'var(--color-text-2)', marginBottom: 28, lineHeight: 1.6 }}>
            You're setting up mid-week. Choose how you'd like to handle {firstName}'s first allowance.
          </p>

          <div className="stack stack-3" style={{ marginBottom: 28 }}>
            {[
              {
                value: true,
                title: new Date().getDay() === 1 ? 'Start next Monday' : 'Start this Monday',
                desc: new Date().getDay() === 1
                  ? `Today is the first day of the week. ${firstName}'s wallet can start next Monday with a full week's allowance.`
                  : `${firstName}'s wallet opens Monday with the full ${parseFloat(allowanceAmount) ? `$${parseFloat(allowanceAmount).toFixed(0)}` : ''} weekly allowance. No activity until then.`,
              },
              {
                value: false,
                title: 'Start right now',
                desc: `${firstName} gets the full allowance today. This week runs through Sunday.`,
              },
            ].map(opt => (
              <div
                key={String(opt.value)}
                onClick={() => setStartOnSunday(opt.value)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 14,
                  padding: '16px', borderRadius: 'var(--radius-lg)', cursor: 'pointer',
                  border: '1.5px solid',
                  borderColor: startOnSunday === opt.value ? 'var(--color-primary)' : 'var(--color-border)',
                  background: startOnSunday === opt.value ? 'var(--color-primary-pale)' : 'white',
                  transition: 'all 150ms ease',
                }}
              >
                <div style={{
                  width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                  border: `2px solid ${startOnSunday === opt.value ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  background: startOnSunday === opt.value ? 'var(--color-primary)' : 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {startOnSunday === opt.value && (
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'white' }} />
                  )}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9375rem',
                    color: startOnSunday === opt.value ? 'var(--color-primary)' : 'var(--color-text)',
                    marginBottom: 4 }}>
                    {opt.title}
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-2)', lineHeight: 1.5 }}>
                    {opt.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            className="btn btn--primary btn--full btn--lg"
            onClick={handleStartNext}
          >
            Continue →
          </button>
        </div>
      )}

      {/* Step 4: Create + Invite */}
      {step === 4 && !inviteLink && (
        <div className="animate-in">
          <button onClick={() => setStep(3)} style={backStyle}>← Back</button>
          <h2 style={{ marginBottom: 8 }}>Ready to create {firstName}'s wallet</h2>
          <p style={{ color: 'var(--color-text-2)', marginBottom: 28 }}>
            {startOnSunday
              ? new Date().getDay() === 1
                ? `${firstName}'s wallet will activate next Monday with a full week's allowance.`
                : `${firstName}'s wallet will activate this Monday with a full week's allowance.`
              : `${firstName}'s wallet will be active immediately.`}
          </p>
          {error && <div className="alert alert--error" style={{ marginBottom: 16 }}>{error}</div>}
          <button
            className="btn btn--primary btn--full btn--lg"
            onClick={handleCreate}
            disabled={loading}
          >
            {loading
              ? <span className="spinner spinner--white" style={{ width: 20, height: 20 }} />
              : 'Create Wallet →'}
          </button>
        </div>
      )}

      {/* Step 4: Invite (after creation) */}
      {step === 4 && inviteLink && (
        <div className="animate-in">
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
            <h2 style={{ marginBottom: 8 }}>{firstName}'s wallet is ready!</h2>
            <p style={{ color: 'var(--color-text-2)' }}>
              Send {firstName} this link to connect their account.
            </p>
          </div>

          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-3)', marginBottom: 8, fontWeight: 600 }}>
              INVITE LINK
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.8125rem',
              wordBreak: 'break-all', color: 'var(--color-text-2)', marginBottom: 16 }}>
              {inviteLink}
            </div>
            <button className="btn btn--secondary btn--full" onClick={copyLink}>
              Copy Link
            </button>
          </div>

          {typeof navigator.share === 'function' && (
            <button
              className="btn btn--primary btn--full"
              style={{ marginBottom: 12 }}
              onClick={() => navigator.share({
                title: `${firstName}'s Wallet invite`,
                text: `Hey ${firstName}! Set up your wallet here:`,
                url: inviteLink,
              })}
            >
              Share Invite
            </button>
          )}

          <button className="btn btn--ghost btn--full" onClick={handleDone}>
            Go to my dashboard →
          </button>
        </div>
      )}
    </div>
  );
};

const backStyle = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--color-text-2)', marginBottom: 24, fontSize: '0.9rem',
  display: 'flex', alignItems: 'center', gap: 6, padding: 0,
  fontFamily: 'var(--font-body)',
};

export default ParentEnrollment;
